import React from 'react';
import {
    IconBrandBluesky,
    IconBrandFacebook,
    IconBrandInstagram,
    IconBrandLinkedin,
    IconBrandMessenger,
    IconBrandPinterest,
    IconBrandTelegram,
    IconBrandWhatsapp,
    IconBrandXing,
    IconMail,
} from '@tabler/icons-react';
import { NavLink } from 'react-router-dom';
import { shareImage } from '../shared/nativeShare.js';
import styles from './SocialShareButton.module.scss';

/**
 * Label and icon per supported share service. Brand colours live in the SCSS map
 * (`$socialShareServiceColors`), so there is only one colour source.
 */
export const SHARE_SERVICE_META = {
    email: { label: 'E-Mail', Icon: IconMail },
    facebook: { label: 'Facebook', Icon: IconBrandFacebook },
    bluesky: { label: 'Bluesky', Icon: IconBrandBluesky },
    whatsApp: { label: 'WhatsApp', Icon: IconBrandWhatsapp },
    facebookMessenger: { label: 'Messenger', Icon: IconBrandMessenger },
    telegram: { label: 'Telegram', Icon: IconBrandTelegram },
    linkedin: { label: 'LinkedIn', Icon: IconBrandLinkedin },
    xing: { label: 'XING', Icon: IconBrandXing },
    pinterest: { label: 'Pinterest', Icon: IconBrandPinterest },
    instagram: { label: 'Instagram', Icon: IconBrandInstagram },
};

// shareType doubles as the utm_source value, so its historical spelling (whatsApp,
// facebookMessenger) is kept; only the analytics class is mapped to its canonical name.
const ANALYTICS_CLASS_SUFFIX = {
    whatsApp: 'whatsapp',
    facebookMessenger: 'messenger',
};

// "Flach" ist die Button-Variante aus Cassiopeia: ein voll breiter, heller Button
// mit Label. Die Bootstrap-Utilities bleiben als Klassen im Markup, damit das
// Aussehen identisch zur bisherigen Darstellung ist.
const FLAT_BUTTON_CLASSES =
    'btn btn-light border d-inline-flex align-items-center justify-content-start text-dark text-decoration-none ps-3 pe-2 py-2 w-100 h-100 shadow-sm rounded-3';

const buildShareTarget = (shareType, { shareUrl, shareText, mailSubject, mailBody }) => {
    switch (shareType) {
        case 'email':
            return {
                href: `mailto:?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`,
            };
        case 'facebook':
            return {
                href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
                isExternal: true,
            };
        case 'bluesky':
            return {
                href: `https://bsky.app/intent/compose?text=${shareText}%0A%0A${encodeURIComponent(shareUrl)}`,
                isExternal: true,
            };
        case 'whatsApp':
            return { href: `whatsapp://send?text=${shareText}%20${encodeURIComponent(shareUrl)}` };
        case 'facebookMessenger':
            return { href: `fb-messenger://share/?link=${encodeURIComponent(shareUrl)}` };
        case 'telegram':
            return {
                href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${shareText}`,
                isExternal: true,
            };
        case 'linkedin':
            return {
                href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
                isExternal: true,
            };
        case 'xing':
            return {
                href: `https://www.xing.com/spi/shares/new?url=${encodeURIComponent(shareUrl)}`,
                isExternal: true,
            };
        case 'pinterest':
            return {
                href: `https://www.pinterest.com/pin/create/bookmarklet/?url=${encodeURIComponent(shareUrl)}`,
                isExternal: true,
            };
        case 'instagram':
            // Instagram has no share URL; the native share sheet takes the configured graphic instead.
            return { isNativeShare: true };
        default:
            return null;
    }
};

/**
 * Einzelner Teilen-Button. Der Kern ist produktneutral: alles, was ein Produkt
 * zusätzlich tun will (z. B. den Förderstufen-Text oder einen Achievement-PATCH
 * in Cassiopeia), kommt über Props von der Aufrufstelle.
 *
 * @param {string} shareType - Dienst-Schlüssel aus SHARE_SERVICE_META
 * @param {'menu'|'icon'|'flat'} variant - Darstellung
 * @param {Function} onShare - optionaler Callback `(shareType) => void`, wird bei jedem Klick ausgelöst
 */
const ShareButton = (props) => {
    const { shareType, variant = 'menu', className = '', pulse = false, onShare } = props;
    const meta = SHARE_SERVICE_META[shareType];

    if (!meta) return <span>Kein Dienst definiert</span>;

    const { label, Icon } = meta;

    const rawShareText = props.shareText
        ? props.shareText
        : props.shareTitle
          ? `Hallo! Unterstütze uns indem du hier für ${props.shareTitle} abstimmst`
          : 'Jetzt für uns abstimmen!';

    const utmParams = `utm_source=${shareType}&utm_medium=social&utm_campaign=${encodeURIComponent(props.campaign || 'Projektdetailsseite')}`;
    let shareUrl = props.shareUrl || props.url;
    if (!shareUrl) {
        const currentHref = typeof window !== 'undefined' ? window.location.href : '';
        shareUrl = currentHref + (currentHref.includes('?') ? `&${utmParams}` : `?${utmParams}`);
    }

    const target = buildShareTarget(shareType, {
        shareUrl,
        shareText: encodeURIComponent(rawShareText),
        mailSubject: props.mailSubject || 'Jetzt für uns abstimmen!',
        mailBody: props.mailBody ? `${props.mailBody}\n${shareUrl}` : `${rawShareText}: ${shareUrl}`,
    });

    if (!target) return <span>Kein Dienst definiert</span>;

    const serviceClass = styles[`Service_${shareType}`] || '';
    // The eTracker analytics hooks into these classes, so every variant carries them.
    const analyticsClass = `anl_social-share--${ANALYTICS_CLASS_SUFFIX[shareType] || shareType}`;
    const handleClick = onShare ? () => onShare(shareType) : undefined;

    const isIcon = variant === 'icon';
    const isFlat = variant === 'flat';
    const classNames = isIcon
        ? `${styles.IconButton} ${serviceClass} ${analyticsClass} ${pulse ? styles.attentionPulse : ''} ${className}`.trim()
        : isFlat
          ? `${FLAT_BUTTON_CLASSES} ${serviceClass} ${analyticsClass} ${className}`.trim()
          : `${styles.MenuItem} ${serviceClass} ${analyticsClass} ${className}`.trim();

    let content;
    if (isIcon) {
        content = (
            <>
                <span className={styles.IconCircle}>
                    <Icon size={24} aria-hidden="true" />
                </span>
                <span className={styles.IconLabel}>{label}</span>
            </>
        );
    } else if (isFlat) {
        content = (
            <>
                <Icon size={24} className={`${styles.FlatIcon} flex-shrink-0`} aria-hidden="true" />
                <span className={`${styles.FlatLabel} ps-2 text-nowrap`}>{label}</span>
            </>
        );
    } else {
        content = (
            <>
                <Icon size={24} className={styles.MenuIcon} aria-hidden="true" />
                <span className={styles.MenuLabel}>{label}</span>
            </>
        );
    }

    if (target.isNativeShare) {
        // Older pages without a configured graphic keep the previous hint link on browsers that
        // cannot open a share sheet, so the button never ends up doing nothing.
        if (!props.shareImageUrl && typeof navigator?.share !== 'function') {
            return (
                <NavLink className={classNames} to={props.instagramUrl || '../tipps'} exact onClick={handleClick}>
                    {content}
                </NavLink>
            );
        }

        return (
            <button
                type="button"
                className={classNames}
                onClick={() => {
                    handleClick?.();
                    shareImage({
                        imageUrl: props.shareImageUrl,
                        title: props.shareTitle,
                        text: rawShareText,
                        url: shareUrl,
                    });
                }}
                aria-label={variant === 'icon' ? `${label} teilen` : undefined}
            >
                {content}
            </button>
        );
    }

    return (
        <a
            href={target.href}
            target="_blank"
            rel={target.isExternal ? 'external noopener noreferrer' : 'noreferrer'}
            className={classNames}
            onClick={handleClick}
            aria-label={variant === 'icon' ? `${label} teilen` : undefined}
        >
            {content}
        </a>
    );
};

export default ShareButton;
